import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';

const TypeBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $type }) => $type === 'advance' ? '#0291ef15' : '#02d3bf15'};
  color: ${({ $type }) => $type === 'advance' ? '#0291ef' : '#02d3bf'};
`;

export const useExpenseCategoryApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/expense-categories?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  // TextInput sayı alanları string döndürür; limitler boşsa null, doluysa
  // number olarak gitmeli (backend decimal? bekliyor).
  const normalizeLimits = (formData) => ({
    ...formData,
    perTransactionLimit: formData.perTransactionLimit === '' || formData.perTransactionLimit == null
      ? null : parseFloat(formData.perTransactionLimit),
    monthlyEmployeeLimit: formData.monthlyEmployeeLimit === '' || formData.monthlyEmployeeLimit == null
      ? null : parseFloat(formData.monthlyEmployeeLimit),
  });

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/expense-categories', normalizeLimits(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/expense-categories/${id}`, normalizeLimits(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/expense-categories/${ids[0]}`);
      } else {
        await api.post('/v1/expense-categories/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const typeLabels = useMemo(() => ({
    expense: t('expense.expense'),
    advance: t('expense.advance'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('expenseCategory.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'type', label: t('expenseCategory.type'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: [t('expense.expense'), t('expense.advance')],
          renderValue: (v) => v,
        },
        filterList: [],
        customFilterListOptions: {
          render: (v) => v,
        },
        customBodyRender: (v) => <TypeBadge $type={v}>{typeLabels[v] || v}</TypeBadge>,
      },
    },
    {
      name: 'perTransactionLimit',
      label: t('expenseCategory.perTransactionLimit'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v == null ? '—' : `${Number(v).toLocaleString('tr-TR')} ₺`),
      },
    },
    {
      name: 'monthlyEmployeeLimit',
      label: t('expenseCategory.monthlyEmployeeLimit'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v == null ? '—' : `${Number(v).toLocaleString('tr-TR')} ₺`),
      },
    },
  ], [t, typeLabels]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('expenseCategory.name'), type: 'text', grid: 12, required: true },
    {
      name: 'type', label: t('expenseCategory.type'), type: 'select', grid: 12, required: true,
      options: [
        { id: 'expense', name: t('expense.expense') },
        { id: 'advance', name: t('expense.advance') },
      ],
      idKey: 'id', labelKey: 'name',
    },
    { name: 'perTransactionLimit', label: t('expenseCategory.perTransactionLimit'), type: 'number', grid: 6 },
    { name: 'monthlyEmployeeLimit', label: t('expenseCategory.monthlyEmployeeLimit'), type: 'number', grid: 6 },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};
